/**
 * Mission Pest Control Theme Configuration
 *
 * Centralized theming configuration matching missionpestcontrol.com design language.
 *
 * Design Language:
 * - Bold, thick typography with serif headings
 * - Vibrant red accent color (#ce2029)
 * - Generous padding and spacing
 * - Rounded corners (moderate border-radius)
 * - Strong visual hierarchy
 */

export default {
  // Company Info
  company: {
    name: 'Mission Pest Control',
    tagline: 'Professional Pest Control Services',
  },

  // Color Palette (from missionpestcontrol.com)
  colors: {
    // Primary Colors
    primary: '#ce2029',           // Vibrant red - main brand color
    primaryDark: '#b91c1c',       // Darker red for hover states
    primaryLight: '#e11d48',      // Lighter red for accents

    // Neutral Colors
    dark: '#3d3d3d',              // Charcoal - for text and dark elements
    light: '#f5f4f2',             // Off-white background
    accent: '#ccc8bd',            // Warm taupe

    // Secondary
    secondary: '#b34044',         // Secondary brand color

    // UI Colors
    background: '#ffffff',
    surface: '#f8fafc',
    text: '#3d3d3d',
    textSecondary: '#6b7280',
    border: '#e5e7eb',

    // Semantic
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },

  // Typography (Matching missionpestcontrol.com exactly)
  typography: {
    // Font Families
    fonts: {
      heading: '"Fugaz One", serif',                     // Display font for headings (as used on site)
      subheading: '"Poltawski Nowy", Georgia, serif',    // Elegant serif for subtitles
      body: '"Lexend Exa", sans-serif',                  // Clean sans-serif for body
      default: '"Lexend Exa", sans-serif',               // Default to body font
    },

    // Font Sizes (Matching mission site exactly)
    sizes: {
      h1: '3.5em',       // 56px (mission site uses em units)
      h2: '2.5em',       // 40px
      h3: '1.5em',       // 24px
      h4: '1.25em',      // 20px
      body: '1em',       // 16px
      small: '0.875rem', // 14px
    },

    // Line Heights (From mission site)
    lineHeights: {
      h1: 1.125,
      h2: 1.25,
      h3: 1.25,
      h4: 1.25,
      body: 1.5,
    },

    // Font Weights (Mission site uses normal weight for headings!)
    weights: {
      normal: 400,      // Used for headings on mission site
      medium: 500,
      semibold: 600,
      bold: 700,        // Used for strong/bold text in body
    },
  },

  // Component Styling (Bold, generous spacing)
  components: {
    // Header
    header: {
      background: '#ce2029',     // Primary red (matches brand color)
      height: '77px',
      logoHeight: '70px',
      padding: '0.5em',
    },

    // Buttons (Matching mission site exactly)
    button: {
      borderRadius: '6px',           // Rounded style (mission uses 2px/6px/100px options)
      padding: '0.65rem 1.5rem',     // From mission site
      fontSize: '1em',               // Mission uses em units
      fontWeight: 400,               // Normal weight (not bold)
      borderWidth: '2px',            // Standard border
      transition: '325ms',           // Smooth transitions (cubic-bezier on mission)
    },

    // Cards (Clean with subtle shadows)
    card: {
      borderRadius: '16px',          // Rounded corners
      padding: '24px',
      background: 'rgba(255, 255, 255, 0.9)',
      border: '1px solid #e5e7eb',
    },

    // Progress Bar
    progressBar: {
      activeColor: '#ce2029',
      inactiveColor: '#ddd',
      height: '7px',
      indicatorSize: '50px',
    },

    // Form Inputs
    input: {
      borderRadius: '8px',
      padding: '12px 16px',
      borderWidth: '2px',
      fontSize: '1rem',
    },
  },

  // Spacing (Generous, bold spacing)
  spacing: {
    section: '4em',      // Large section padding
    container: '24px',
    card: '24px',
    element: '16px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
  },

  // Assets (all local paths - no remote URLs)
  assets: {
    logo: {
      path: '@/assets/mission/mission-logo.svg',
      alt: 'Mission Pest Control Logo',
      height: '105px',
    },
    mascot: '@/assets/mission/mission-anteater.png',
  },

  // Vuetify Theme
  vuetify: {
    primary: '#ce2029',
    secondary: '#b34044',
    accent: '#e11d48',
    error: '#ef4444',
    info: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
  },

  // API Configuration
  api: {
    baseUrl: {
      development: 'http://localhost:3000/api',
      production: 'https://api.getpestcontrol.services/api',
    },
  },

  // Company-Specific Settings
  settings: {
    officeIDs: '714',
    leadSourceID: '10207', // "Mission Website eCommerce"
  },
}
